import React from 'react';
import { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { X, AlertTriangle } from 'lucide-react';
import { BankAccount } from '../types';

const FEE_TYPES = [
  'Monthly Service Fee',
  'Overdraft Fee',
  'NSF Fee (Non-Sufficient Funds)',
  'ATM Fee',
  'Wire Transfer Fee',
  'Foreign Transaction Fee',
  'Other Fee',
] as const;

interface AccountFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string;
  householdId: string;
  account: BankAccount | null;
}

/**
 * Quick-entry modal, scoped to one bank account, for the small debits that
 * don't belong in the general Transaction modal's flow -- service fees,
 * overdraft fees, NSFs, ATM/wire fees. Writes a plain 'spend' transaction
 * linked to this account (so it shows up in the account's own transaction
 * list) and decrements the account's currentBalance directly, the same way
 * any other cash spend against a bank account does.
 */
export default function AccountFeeModal({ isOpen, onClose, userId, userEmail, householdId, account }: AccountFeeModalProps) {
  const [feeType, setFeeType] = useState<typeof FEE_TYPES[number]>('Overdraft Fee');
  const [customLabel, setCustomLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFeeType('Overdraft Fee');
      setCustomLabel('');
      setAmount('');
      setDate(new Date().toISOString().split('T')[0]);
      setNote('');
    }
  }, [isOpen]);

  if (!isOpen || !account) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || isNaN(Number(amount))) return;

    setLoading(true);
    try {
      const numAmount = Number(amount);
      const [year, month, day] = date.split('-');
      const txDate = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0);
      const category = feeType === 'Other Fee' && customLabel.trim() ? customLabel.trim() : feeType;

      await addDoc(collection(db, 'transactions'), {
        userId,
        userEmail,
        householdId,
        type: 'spend',
        amount: numAmount,
        category,
        description: note || `${category} - ${account.name}`,
        linkedId: null,
        occurrenceDate: null,
        date: txDate,
        paymentMethod: 'cash',
        creditCardId: null,
        bankAccountId: account.id,
        toBankAccountId: null,
      });

      await updateDoc(doc(db, 'bank_accounts', account.id!), {
        currentBalance: increment(-numAmount)
      });

      onClose();
    } catch (err) {
      console.error('Error recording account fee:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Record a Fee
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{account.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Fee Type</label>
            <select
              value={feeType}
              onChange={(e) => setFeeType(e.target.value as typeof FEE_TYPES[number])}
              className="w-full px-4 py-2 bg-amber-50 border border-amber-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
            >
              {FEE_TYPES.map(ft => <option key={ft} value={ft}>{ft}</option>)}
            </select>
          </div>

          {feeType === 'Other Fee' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Describe the fee</label>
              <input
                type="text"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="e.g. Paper Statement Fee"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Note (Optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Brief details..."
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-amber-600 text-white rounded-md font-semibold text-sm hover:bg-amber-700 disabled:opacity-50 mt-4 shadow-sm transition-colors"
          >
            {loading ? 'Recording...' : 'Record Fee'}
          </button>
        </form>
      </div>
    </div>
  );
}
